// /var/www/oszczedzam/client/src/hooks/useGoals.js
import { useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, push, get, update, remove, set } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import { useTransactions } from './useTransactions';

export function useGoals() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const { addTransaction, updateTransaction } = useTransactions();

  // Pobierz wszystkie cele oszczędnościowe
  const fetchGoals = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const goalsRef = ref(database, `users/${currentUser.uid}/goals`);
      const snapshot = await get(goalsRef);
      
      const goalsData = [];
      
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          goalsData.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
      }
      
      setGoals(goalsData);
      setError(null);
    } catch (err) {
      console.error("Błąd podczas pobierania celów:", err);
      setError('Nie udało się pobrać celów oszczędnościowych.');
    } finally {
      setLoading(false);
    }
  };

  // Dodaj nowy cel oszczędnościowy
  const addGoal = async (goalData) => {
    if (!currentUser) return null;
    
    try {
      const goalsRef = ref(database, `users/${currentUser.uid}/goals`);
      
      // Przygotuj dane celu
      const newGoalData = {
        ...goalData,
        createdAt: new Date().toISOString(),
        savingsHistory: []
      };
      
      // Dodaj nowy cel do bazy danych
      const newGoalRef = await push(goalsRef, newGoalData);
      
      const newGoal = {
        id: newGoalRef.key,
        ...newGoalData
      };
      
      setGoals([...goals, newGoal]);
      return newGoal;
    } catch (err) {
      console.error("Błąd podczas dodawania celu:", err);
      throw err;
    }
  };

  // Zaktualizuj cel oszczędnościowy
  const updateGoal = async (goalId, goalData) => {
    if (!currentUser) return null;
    
    try {
      const goalRef = ref(database, `users/${currentUser.uid}/goals/${goalId}`);
      
      // Sprawdź, czy cel istnieje
      const snapshot = await get(goalRef);
      if (!snapshot.exists()) {
        throw new Error("Cel oszczędnościowy nie istnieje");
      }
      
      const existingGoal = { id: goalId, ...snapshot.val() };
      
      // Przygotuj dane do aktualizacji
      const updatedGoalData = {
        ...goalData,
        updatedAt: new Date().toISOString()
      };
      
      // Jeśli zmieniono kwotę bieżącą, dodaj wpis do historii oszczędności
      if (existingGoal.currentAmount !== goalData.currentAmount) {
        const difference = goalData.currentAmount - existingGoal.currentAmount;
        
        // Tylko jeśli jest faktyczna zmiana
        if (difference !== 0) {
          // Utwórz nowy wpis historii oszczędności
          const historyEntry = {
            date: new Date().toISOString(),
            amount: difference,
            description: difference > 0 ? 'Dodanie oszczędności' : 'Wycofanie oszczędności'
          };
          
          // Dodaj wpis do tablicy historii
          const savingsHistory = existingGoal.savingsHistory || [];
          updatedGoalData.savingsHistory = [...savingsHistory, historyEntry];
        } else {
          // Zachowaj istniejącą historię
          updatedGoalData.savingsHistory = existingGoal.savingsHistory || [];
        }
      } else {
        // Zachowaj istniejącą historię
        updatedGoalData.savingsHistory = existingGoal.savingsHistory || [];
      }
      
      // Aktualizuj cel w bazie danych
      await update(goalRef, updatedGoalData);
      
      // Zaktualizuj stan lokalny
      const updatedGoals = goals.map(g => 
        g.id === goalId 
          ? { ...g, ...updatedGoalData } 
          : g
      );
      
      setGoals(updatedGoals);
      
      return { id: goalId, ...updatedGoalData };
    } catch (err) {
      console.error("Błąd podczas aktualizacji celu:", err);
      throw err;
    }
  };

  // Usuń cel oszczędnościowy
  const deleteGoal = async (goalId) => {
    if (!currentUser) return false;
    
    try {
      const goalRef = ref(database, `users/${currentUser.uid}/goals/${goalId}`);
      
      // Sprawdź, czy cel istnieje
      const snapshot = await get(goalRef);
      if (!snapshot.exists()) {
        throw new Error("Cel oszczędnościowy nie istnieje");
      }
      
      // Usuń cel z bazy danych
      await remove(goalRef);
      
      // Zaktualizuj stan lokalny
      setGoals(goals.filter(g => g.id !== goalId));
      
      return true;
    } catch (err) {
      console.error("Błąd podczas usuwania celu:", err);
      throw err;
    }
  };

  // Dodaj oszczędności do celu
  const allocateSavings = async (goalId, allocationData) => {
    if (!currentUser) return null;
    
    try {
      const goalRef = ref(database, `users/${currentUser.uid}/goals/${goalId}`);
      
      // Sprawdź, czy cel istnieje
      const snapshot = await get(goalRef);
      if (!snapshot.exists()) {
        throw new Error("Cel oszczędnościowy nie istnieje");
      }
      
      const existingGoal = { id: goalId, ...snapshot.val() };
      
      // Przygotuj historię oszczędności
      const savingsHistory = existingGoal.savingsHistory || [];
      const historyEntry = {
        date: allocationData.date || new Date().toISOString(),
        amount: allocationData.amount,
        description: allocationData.description || 'Dodanie oszczędności',
        transactionId: allocationData.transactionId || null
      };
      
      // Oblicz nową kwotę bieżącą
      const newCurrentAmount = existingGoal.currentAmount + allocationData.amount;
      
      // Przygotuj dane do aktualizacji
      const updatedGoalData = {
        ...existingGoal,
        currentAmount: newCurrentAmount,
        progress: (newCurrentAmount / existingGoal.targetAmount) * 100,
        savingsHistory: [...savingsHistory, historyEntry],
        updatedAt: new Date().toISOString()
      };
      
      // Obsługa różnych źródeł oszczędności
      if (allocationData.source === 'newTransaction') {
        // Utwórz nową transakcję
        const transactionData = {
          title: `Oszczędności - ${existingGoal.name}`,
          amount: -allocationData.amount, // Ujemna wartość (wydatek)
          category: 'Oszczędności',
          date: allocationData.date || new Date().toISOString().split('T')[0],
          description: allocationData.description,
          isRecurring: false,
          tags: ['oszczędności', existingGoal.name],
          relatedGoalId: goalId
        };
        
        // Dodaj transakcję
        const newTransaction = await addTransaction(transactionData);
        
        // Zaktualizuj odniesienie do transakcji w historii oszczędności
        updatedGoalData.savingsHistory[updatedGoalData.savingsHistory.length - 1].transactionId = newTransaction.id;
      }
      else if (allocationData.source === 'existingTransaction' && allocationData.transactionId) {
        // TODO: Można by tu dodać logikę aktualizacji istniejącej transakcji, jeśli potrzeba
        // np. dodanie tagu lub odniesienia do celu
      }
      
      // Aktualizuj cel w bazie danych
      await update(goalRef, updatedGoalData);
      
      // Zaktualizuj stan lokalny
      const updatedGoals = goals.map(g => 
        g.id === goalId 
          ? { ...g, ...updatedGoalData } 
          : g
      );
      
      setGoals(updatedGoals);
      
      return { id: goalId, ...updatedGoalData };
    } catch (err) {
      console.error("Błąd podczas alokacji oszczędności:", err);
      throw err;
    }
  };

  // Pobierz cele przy pierwszym renderowaniu
  useEffect(() => {
    fetchGoals();
  }, [currentUser]);

  return {
    goals,
    loading,
    error,
    fetchGoals,
    addGoal,
    updateGoal,
    deleteGoal,
    allocateSavings
  };
}